import { useState } from 'react';
import { X, Plus, User, Trash2, Loader2 } from 'lucide-react';
import { useSignersModal } from '../../hooks/use-signers-modal.hook';
import axios from 'axios';
import { signerApi } from '@/features/application/api/signer.api';
import { logger } from '@/features/help/utils/logger';
import { Signer } from '@/features/application/types/signers';

interface SignersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: (emails: string[]) => Promise<void> | void;
  userShortId: string;
  mappingShortId: string;
  crmId: string | null;
  prefetchedSigners?: Signer[];
  hasPrefetchedSigners?: boolean;
  isPrefetchingSigners?: boolean;
}

// Purpose: Modal component for managing additional owner emails and triggering invitations
export const SignersModal = ({
  isOpen,
  onClose,
  onConfirm,
  userShortId,
  mappingShortId,
  crmId,
  prefetchedSigners,
  hasPrefetchedSigners,
  isPrefetchingSigners,
}: SignersModalProps) => {
  const [isNotifying, setIsNotifying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deleteErrors, setDeleteErrors] = useState<Record<string, string>>({});
  const [deletingSignerIds, setDeletingSignerIds] = useState<Record<string, boolean>>({});
  const {
    signers,
    addSigner,
    removeSigner,
    updateSignerEmail,
    isSubmissionAllowed,
    submitSigners,
    isLoading,
    isFetchingSigners,
    submissionEmails,
  } = useSignersModal({
    isOpen,
    userShortId,
    mappingShortId,
    crmId,
    initialSigners: prefetchedSigners,
    hasPrefetchedSigners,
  });
  const isLoadingSigners = isFetchingSigners || !!isPrefetchingSigners;
  const shouldShowLoadingRows = isLoadingSigners && signers.length === 0;
  const loadingRows = Array.from({ length: 2 });

  // Purpose: Determine whether a signer row is local-only
  const isLocalSigner = (signerId: string) => {
    return signerId.startsWith('local-');
  };

  // Purpose: Close modal and clear transient UI state
  const handleModalClose = () => {
    setDeleteErrors({});
    setDeletingSignerIds({});
    setErrorMessage(null);
    onClose();
  };

  // Purpose: Remove a signer row, deleting server records when needed
  const handleRemoveSigner = async (signerId: string) => {
    if (isLocalSigner(signerId)) {
      removeSigner(signerId);
      setDeleteErrors((prev) => {
        const next = { ...prev };
        delete next[signerId];
        return next;
      });
      return;
    }

    if (!crmId) {
      setDeleteErrors((prev) => ({
        ...prev,
        [signerId]: 'Missing application record. Please refresh and try again.',
      }));
      logger.warn('SignersModal', 'Delete signer blocked: missing crmId', {
        signerId,
        userShortId,
        mappingShortId,
      });
      return;
    }

    setDeletingSignerIds((prev) => ({ ...prev, [signerId]: true }));
    setDeleteErrors((prev) => {
      const next = { ...prev };
      delete next[signerId];
      return next;
    });

    logger.info('SignersModal', 'Deleting additional owner signer', {
      signerId,
      userShortId,
      mappingShortId,
      crmId,
    });

    try {
      await signerApi.deleteSigner(userShortId, mappingShortId, crmId, signerId);
      removeSigner(signerId);
    } catch (error) {
      logger.error('SignersModal', 'Failed to delete additional owner signer', {
        error,
        signerId,
        userShortId,
        mappingShortId,
        crmId,
      });
      setDeleteErrors((prev) => ({
        ...prev,
        [signerId]: 'Unable to remove owner. Please try again.',
      }));
    } finally {
      setDeletingSignerIds((prev) => ({ ...prev, [signerId]: false }));
    }
  };

  // Purpose: Submit additional signer invitations and notify parent of confirmed emails
  const handleModalSubmit = async () => {
    if (isLoading || isNotifying) return;

    let success = false;
    try {
      success = await submitSigners();
      if (!success) return;

      // Optional callback for parent flows (not required for invitations to work)
      if (onConfirm) {
        setIsNotifying(true);
        await onConfirm(submissionEmails);
      }
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 409 &&
        error.response?.data?.error === 'duplicate_signer'
      ) {
        setErrorMessage('This email is already registered as a signer for this application.');
        logger.warn('SignersModal', 'Duplicate signer error when submitting', {
          error,
        });
      } else {
        setErrorMessage('Failed to update signers. Please try again.');
        logger.error('SignersModal', 'Unexpected error when submitting signers', {
          error,
        });
      }
    } finally {
      setIsNotifying(false);
      if (success) {
        handleModalClose();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="signers-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 px-4"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 text-card-foreground">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-foreground">Additional Owners</h2>
          <button onClick={handleModalClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Additional Owners */}
          <div className="pt-2">
            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
              <User className="h-5 w-5 text-muted-foreground" />
              Additional Owners
              {isLoadingSigners && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </label>
            <p className="mb-3 text-sm text-muted-foreground">
              Please add email addresses for any additional owners who need to sign this
              application.
            </p>

            <div className="space-y-3 min-h-[112px]">
              {shouldShowLoadingRows
                ? loadingRows.map((_, index) => (
                    <div key={`signer-loading-${index}`} className="flex gap-2">
                      <div className="h-10 flex-1 rounded-md border border-input bg-muted/40 animate-pulse" />
                      <div className="h-10 w-10 rounded-md border border-input bg-muted/40 animate-pulse" />
                    </div>
                  ))
                : signers.map((signer) => {
                    const isLocalRow = isLocalSigner(signer.signer_id);
                    const isDeleting = deletingSignerIds[signer.signer_id];
                    const rowError = deleteErrors[signer.signer_id];

                    return (
                      <div key={signer.signer_id} className="space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="email"
                            value={signer.email || ''}
                            onChange={(e) =>
                              isLocalRow
                                ? updateSignerEmail(signer.signer_id, e.target.value)
                                : undefined
                            }
                            placeholder="owner@example.com"
                            readOnly={!isLocalRow}
                            aria-readonly={!isLocalRow}
                            className={`flex-1 rounded-md border border-input px-3 py-2 text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
                              isLocalRow ? 'bg-background' : 'bg-background text-muted-foreground'
                            }`}
                          />
                          <button
                            onClick={() => handleRemoveSigner(signer.signer_id)}
                            disabled={isDeleting}
                            className="inline-flex items-center justify-center rounded-md border border-transparent bg-background p-2 text-destructive transition-colors hover:bg-destructive hover:text-destructive-foreground disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {isDeleting ? (
                              <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                              <Trash2 className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                        {rowError && (
                          <p className="text-xs text-destructive-foreground">{rowError}</p>
                        )}
                      </div>
                    );
                  })}
            </div>

            <button
              onClick={addSigner}
              className="mt-4 inline-flex items-center rounded-md bg-secondary px-3 py-2 text-sm font-medium text-secondary-foreground transition-colors hover:bg-secondary/80"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Another Owner
            </button>
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mt-4 rounded-md border border-destructive/20 bg-destructive/10 p-3">
            <div className="text-sm font-medium text-destructive-foreground">{errorMessage}</div>
          </div>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={handleModalClose}
            className="rounded-md border border-border bg-secondary px-4 py-2 text-secondary-foreground transition-colors hover:bg-secondary/80"
          >
            Cancel
          </button>
          <button
            onClick={handleModalSubmit}
            disabled={!isSubmissionAllowed || isLoading || isNotifying}
            data-testid="signers-modal-confirm-button"
            className={`rounded-md bg-accent px-4 py-2 text-accent-foreground transition-colors hover:bg-accent/90 ${
              !isSubmissionAllowed || isLoading || isNotifying
                ? 'cursor-not-allowed opacity-50'
                : ''
            }`}
          >
            {isLoading || isNotifying ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};
